import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import {
  Plus,
  Trash,
  PencilSimple,
  Eye,
  EyeSlash,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { formatNumber } from "@/lib/utils-format";
import Modal from "@/components/Modal";
import MediaUpload from "@/components/MediaUpload";

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [view, setView] = useState("order");
  const [draggedId, setDraggedId] = useState(null);
  const [dropId, setDropId] = useState(null);
  const [orderSaved, setOrderSaved] = useState(false);
  const { admin } = useAuth();
  const canReorder = ["super_admin", "admin"].includes(admin?.role);
  const activeItems = items.filter((event) => event.is_active);
  const moveEvent = async (id, target) => {
    if (ordering || !canReorder) return;
    const index = activeItems.findIndex((event) => event.id === id);
    if (index < 0 || target < 0 || target >= activeItems.length || target === index) return;
    const reordered = [...activeItems];
    reordered.splice(target, 0, reordered.splice(index, 1)[0]);
    let activeIndex = 0;
    const next = items.map((event) => event.is_active ? reordered[activeIndex++] : event);
    setOrdering(true);
    setOrderError("");
    setOrderSaved(false);
    try {
      await api.put("/events/order", { ids: next.map((event) => event.id) });
      setItems(next);
      setOrderSaved(true);
    } catch (error) {
      setOrderError(error.response?.data?.detail || "Не удалось сохранить порядок. Повторите попытку.");
    } finally {
      setOrdering(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/events");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (id) => {
    await api.post(`/events/${id}/toggle`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить ивент со всеми регистрациями?")) return;
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div data-testid="events-page">
      <PageHeader
        title="Мероприятия"
        subtitle="Управление ивентами и регистрациями"
        actions={
          <button
            data-testid="create-event-btn"
            disabled={ordering}
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
          >
            <Plus size={14} weight="bold" /> Новый ивент
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="Режим отображения мероприятий">
        {[["order", `Порядок в боте · ${activeItems.length}`], ["all", `Все мероприятия · ${items.length}`]].map(([value, label]) => (
          <button key={value} onClick={() => setView(value)} disabled={ordering} aria-pressed={view === value}
            className={`px-3 py-2 rounded-md text-sm border ${view === value ? "bg-blue-500/10 text-blue-400 border-blue-500/30" : "border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}>
            {label}
          </button>
        ))}
      </div>
      {orderError && <p role="alert" className="text-sm text-red-400 mb-4">{orderError}</p>}
      <p role="status" className="text-xs text-zinc-400 mb-3 min-h-4">
        {ordering ? "Сохраняем порядок…" : orderSaved ? "Порядок сохранён" : ""}
      </p>

      {loading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : view === "order" ? (
        <div className="max-w-3xl">
          <p className="text-sm text-zinc-400 mb-4">
            Только активные эфиры — в том порядке, в котором их видят в боте.
            {canReorder && " Перетащите эфир за значок слева или выберите нужное место по номеру. Изменения сохраняются автоматически."}
          </p>
          {activeItems.length === 0 ? (
            <p className="text-zinc-500 text-sm py-8">Нет активных эфиров. Откройте «Все мероприятия», чтобы включить нужные.</p>
          ) : (
            <ol className="space-y-2" aria-label="Порядок активных эфиров" aria-busy={ordering}>
              {activeItems.map((event, index) => (
                <li key={event.id} data-testid={`event-order-${event.id}`}
                  onDragOver={(e) => {
                    if (draggedId === null || ordering) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDropId(event.id);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (draggedId !== null) moveEvent(draggedId, index);
                    setDraggedId(null);
                    setDropId(null);
                  }}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition-colors ${dropId === event.id && draggedId !== event.id ? "border-blue-500 bg-blue-500/10" : "border-zinc-800 bg-zinc-900/60"} ${draggedId === event.id ? "opacity-40" : ""}`}>
                  {canReorder && (
                    <span draggable={!ordering}
                      title="Перетащить эфир"
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", String(event.id));
                        e.dataTransfer.effectAllowed = "move";
                        setDraggedId(event.id);
                        setOrderSaved(false);
                      }}
                      onDragEnd={() => { setDraggedId(null); setDropId(null); }}
                      className={`text-zinc-500 p-1 ${ordering ? "cursor-wait" : "cursor-grab active:cursor-grabbing"}`}>
                      <DotsSixVertical size={20} />
                    </span>
                  )}
                  {canReorder ? (
                    <select value={index} disabled={ordering}
                      aria-label={`Место эфира «${event.btn_name}»`}
                      onChange={(e) => moveEvent(event.id, Number(e.target.value))}
                      className="w-16 shrink-0 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm disabled:opacity-50">
                      {activeItems.map((_, position) => <option key={position} value={position}>{position + 1}</option>)}
                    </select>
                  ) : <span className="text-zinc-500 w-6">{index + 1}</span>}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium break-words">{event.btn_name}</div>
                    <div className="text-xs text-zinc-500 truncate">#{event.id} · {event.full_name}</div>
                  </div>
                  <button onClick={() => setEditing(event)} disabled={ordering}
                    aria-label={`Редактировать ${event.btn_name}`}
                    className="p-2 text-zinc-400 hover:text-blue-400 disabled:opacity-50">
                    <PencilSimple size={18} />
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-sm py-12 text-center">
          Пока нет ивентов
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((e) => (
            <div
              key={e.id}
              data-testid={`event-card-${e.id}`}
              className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-5 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">
                    #{e.id} {e.is_active ? "· активен" : "· скрыт"}
                  </div>
                  <h3 className="font-semibold mt-1 leading-tight">
                    {e.btn_name}
                  </h3>
                  <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                    {e.full_name}
                  </div>
                </div>
                <div
                  className={`w-2.5 h-2.5 rounded-full mt-2 ${
                    e.is_active
                      ? "bg-emerald-500 shadow-[0_0_10px_#10b981]"
                      : "bg-zinc-600"
                  }`}
                />
              </div>

              <div className="grid grid-cols-3 gap-2 my-4">
                <Stat label="Регистраций" value={e.regs} />
                <Stat label="Начали" value={e.started} />
                <Stat label="Каналов" value={e.channels?.length || 0} />
              </div>

              <div className="flex flex-wrap gap-1 mb-3 text-xs">
                <Chip>{e.content_type}</Chip>
                {e.require_email ? (
                  <Chip color="#10b981">email обязателен</Chip>
                ) : (
                  <Chip>без email</Chip>
                )}
                {e.channels?.slice(0, 2).map((c) => (
                  <Chip key={c} color="#2AABEE">
                    {c}
                  </Chip>
                ))}
              </div>

              <div className="flex items-center gap-1 border-t border-zinc-800 pt-3">
                <button
                  onClick={() => toggle(e.id)}
                  data-testid={`toggle-event-${e.id}`}
                  className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-zinc-800 hover:border-zinc-700"
                >
                  {e.is_active ? (
                    <>
                      <EyeSlash size={12} /> Скрыть
                    </>
                  ) : (
                    <>
                      <Eye size={12} /> Показать
                    </>
                  )}
                </button>
                <button
                  onClick={() => setEditing(e)}
                  data-testid={`edit-event-${e.id}`}
                  className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-zinc-800 hover:border-blue-500/50 hover:text-blue-400"
                >
                  <PencilSimple size={12} /> Изменить
                </button>
                <button
                  onClick={() => remove(e.id)}
                  data-testid={`delete-event-${e.id}`}
                  className="ml-auto text-zinc-500 hover:text-red-400 p-1.5"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventModal
          event={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-950/60 rounded-md px-2 py-1.5 border border-zinc-900">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="font-mono text-sm">{formatNumber(value || 0)}</div>
    </div>
  );
}

function Chip({ children, color = "#a1a1aa" }) {
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-mono border"
      style={{
        background: `${color}15`,
        color,
        borderColor: `${color}40`,
      }}
    >
      {children}
    </span>
  );
}

function EventModal({ event, onClose, onSaved }) {
  const isNew = !event.id;
  const [form, setForm] = useState({
    full_name: event.full_name || "",
    btn_name: event.btn_name || "",
    success_msg: event.success_msg || "",
    content_type: event.content_type || "text",
    file_id: event.file_id || "",
    success_btns: (event.success_btns || []),
    channels: event.channels || [],
    require_email: !!event.require_email,
    is_active: event.is_active === undefined ? true : !!event.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { ...form };
      if (typeof payload.channels === "string") {
        payload.channels = payload.channels
          .split(/[\s,\n]+/)
          .map((c) => c.trim())
          .filter(Boolean);
      }
      if (isNew) await api.post("/events", payload);
      else await api.put(`/events/${event.id}`, payload);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={isNew ? "Новый" : `Ивент #${event.id}`}
      title={form.btn_name || "Новый ивент"}
      testid="event-modal"
      maxWidth="2xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md border border-zinc-800 hover:border-zinc-700"
          >
            Отмена
          </button>
          <button
            data-testid="save-event-btn"
            disabled={saving || !form.btn_name.trim()}
            onClick={save}
            className="px-3 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-medium"
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Полное название (для отчётов)">
          <input
            data-testid="event-fullname-input"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Название кнопки (видно пользователю)">
          <input
            data-testid="event-btnname-input"
            value={form.btn_name}
            onChange={(e) => setForm({ ...form, btn_name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Фото или видео (необязательно, отправляется вместе с сообщением успеха)">
          <MediaUpload
            testid="event-media-upload"
            accept="any"
            value={form.file_id && /^(\/api\/|https?:\/\/)/.test(form.file_id) ? form.file_id : ""}
            onChange={(url, kind) =>
              setForm({
                ...form,
                file_id: url,
                content_type: kind || (url ? "photo" : "text"),
              })
            }
            helpText="Изображение (до 8 MB) или видео (до 20 MB). Файл будет отправлен через Telegram при регистрации пользователя."
          />
        </Field>

        <Field label="Сообщение успеха (HTML, поддерживается форматирование)">
          <textarea
            data-testid="event-msg-textarea"
            value={form.success_msg || ""}
            onChange={(e) =>
              setForm({ ...form, success_msg: e.target.value })
            }
            rows={6}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
          />
        </Field>

        <Field label="Каналы для подписки (через пробел или новую строку)">
          <textarea
            data-testid="event-channels-textarea"
            value={
              typeof form.channels === "string"
                ? form.channels
                : form.channels.join(" ")
            }
            onChange={(e) => setForm({ ...form, channels: e.target.value })}
            rows={2}
            placeholder="@channel1 @channel2"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
          />
        </Field>

        <Field label="Кнопки в сообщении успеха (Текст — URL)">
          <div className="space-y-1.5">
            {form.success_btns.map((b, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <input
                  value={b.text}
                  onChange={(e) => {
                    const arr = [...form.success_btns];
                    arr[i] = { ...arr[i], text: e.target.value };
                    setForm({ ...form, success_btns: arr });
                  }}
                  placeholder="Текст"
                  className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                />
                <input
                  value={b.url || ""}
                  onChange={(e) => {
                    const arr = [...form.success_btns];
                    arr[i] = { ...arr[i], url: e.target.value };
                    setForm({ ...form, success_btns: arr });
                  }}
                  placeholder="https://"
                  className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
                />
                <button
                  onClick={() => {
                    const arr = form.success_btns.filter((_, j) => j !== i);
                    setForm({ ...form, success_btns: arr });
                  }}
                  className="text-zinc-500 hover:text-red-400 p-1.5"
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setForm({
                  ...form,
                  success_btns: [...form.success_btns, { text: "", url: "" }],
                })
              }
              className="text-xs text-zinc-400 hover:text-blue-400 inline-flex items-center gap-1"
            >
              <Plus size={12} /> Добавить кнопку
            </button>
          </div>
        </Field>

        <div className="flex items-center gap-6">
          <Switch
            testid="event-require-email"
            label="Запрашивать email"
            value={form.require_email}
            onChange={(v) => setForm({ ...form, require_email: v })}
          />
          <Switch
            testid="event-is-active"
            label="Активен"
            value={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
        </div>

        {err && (
          <div className="text-sm text-red-400" data-testid="event-error">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Switch({ label, value, onChange, testid }) {
  return (
    <label
      data-testid={testid}
      className="flex items-center gap-2 cursor-pointer text-sm select-none"
    >
      <span
        className={`w-9 h-5 rounded-full relative transition-colors ${
          value ? "bg-blue-500" : "bg-zinc-800"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-zinc-50 transition-transform ${
            value ? "translate-x-4" : ""
          }`}
        />
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      {label}
    </label>
  );
}
